<template>
    <div>
        <div v-if="isLoading" class="flex h-40 w-full items-center justify-center">
            <span class="spinner"></span>
        </div>

        <div v-else-if="stories.length === 0" class="flex h-40 w-full items-center justify-center">
            <h3 class="text-sm font-medium text-gray-700">
                No stories found. Create your first story to get started!
            </h3>
        </div>

        <cube-provider v-else>
            <template v-slot="{ cubeApi }">
                <query-renderer :query="metricsQuery" :cubeApi="cubeApi">
                    <template v-slot="{ loading: metricsLoading, resultSet }">
                        <div class="story-grid-container flex gap-8 overflow-x-auto pb-4 pl-8 pr-8">
            <div
                v-for="story in stories"
                :key="story.id"
                @click="editStory(story.id)"
                class="story-card flex-shrink-0 bg-white border border-gray-200 rounded-lg shadow-sm transition-shadow duration-200 cursor-pointer overflow-visible"
            >
                <div class="relative aspect-[9/16] bg-gray-100 story-container">
                    <!-- Fan-out pages behind main video (only if multiple pages) -->
                    <div
                        v-if="story.pages && story.pages.length > 1"
                        class="fan-out-pages"
                    >
                        <!-- Left fan-out page (first page) -->
                        <div
                            v-if="story.pages.length > 1"
                            class="fan-page fan-page-left"
                            :style="getPageBackgroundStyle(story, 0)"
                        ></div>
                        <!-- Right fan-out page (second page) -->
                        <div
                            v-if="story.pages.length > 2"
                            class="fan-page fan-page-right"
                            :style="getPageBackgroundStyle(story, 1)"
                        ></div>
                    </div>
                    
                    <!-- Main video card -->
                    <div class="main-video-card relative w-full h-full bg-gray-100 rounded-lg overflow-hidden">
                        <hls-video-player
                            v-if="story.muxAsset && story.muxAsset.playbackId"
                            :playback-id="story.muxAsset.playbackId"
                            :play-on-hover="true"
                            :muted="true"
                            :loop="true"
                            :playsinline="true"
                            :show-play-overlay="true"
                            :alt="story.title"
                            class="absolute inset-0"
                        />
                        <div
                            v-else
                            class="absolute inset-0 flex items-center justify-center text-gray-400"
                        >
                            <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div class="absolute top-2 right-2 z-10">
                            <span
                                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                :class="story.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                            >
                                {{ story.active ? 'Active' : 'Inactive' }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="p-2.5">
                    <h3 class="text-sm font-semibold text-gray-900 mb-2 truncate">
                        {{ story.title }}
                    </h3>

                    <div class="space-y-2">
                        <div class="flex items-center justify-between text-xs text-gray-600">
                            <span class="flex items-center">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                </svg>
                                Pages
                            </span>
                            <span class="font-medium">{{ story.pages ? story.pages.length : 0 }}</span>
                        </div>

                        <div class="flex items-center justify-between text-xs text-gray-600">
                            <span class="flex items-center">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Venues
                            </span>
                            <span class="font-medium">{{ story.venueAssignments ? story.venueAssignments.length : 0 }}</span>
                        </div>

                        <div class="flex items-center justify-between text-xs text-gray-600">
                            <span class="flex items-center">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                Views
                            </span>
                            <span class="font-medium">
                                <span v-if="metricsLoading" class="inline-block h-3 w-3 spinner-small"></span>
                                <span v-else>{{ getMetricValue(story.id, 'views', resultSet) }}</span>
                            </span>
                        </div>

                        <div class="flex items-center justify-between text-xs text-gray-600">
                            <span class="flex items-center">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                </svg>
                                Clicks
                            </span>
                            <span class="font-medium">
                                <span v-if="metricsLoading" class="inline-block h-3 w-3 spinner-small"></span>
                                <span v-else>{{ getMetricValue(story.id, 'clicks', resultSet) }}</span>
                            </span>
                        </div>
                    </div>

                    <div class="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-500">
                        Updated {{ formatDate(story.updated_at) }}
                    </div>
                </div>
            </div>
                        </div>
                    </template>
                </query-renderer>
            </template>
        </cube-provider>
    </div>
</template>

<script>
import HlsVideoPlayer from './common/HlsVideoPlayer';
import { CubeProvider, QueryRenderer } from '@/components/cubejs';

export default {
    name: 'StoryGrid',
    components: {
        HlsVideoPlayer,
        CubeProvider,
        QueryRenderer
    },
    props: {
        stories: {
            type: Array,
            required: true,
            default: () => []
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        storyIds() {
            return this.stories.map(story => String(story.id));
        },
        metricsQuery() {
            if (!this.storyIds.length) {
                return null;
            }
            
            return {
                measures: ['events.count'],
                dimensions: ['events.story_id', 'events.event'],
                filters: [
                    {
                        member: 'events.story_id',
                        operator: 'equals',
                        values: this.storyIds
                    },
                    {
                        member: 'events.event',
                        operator: 'equals',
                        values: ['story_viewed', 'story_cta_clicked']
                    }
                ]
            };
        }
    },
    methods: {
        getMetricValue(storyId, metricType, resultSet) {
            if (!resultSet) {
                return 0;
            }
            
            const eventName = metricType === 'views' ? 'story_viewed' : 'story_cta_clicked';
            const storyIdStr = String(storyId);
            
            const data = resultSet.rawData();
            const row = data.find(r => 
                String(r['events.story_id']) === storyIdStr && 
                r['events.event'] === eventName
            );
            
            if (!row) {
                return 0;
            }
            
            const count = row['events.count'];
            return typeof count === 'number' ? count : parseInt(count, 10) || 0;
        },
        editStory(storyId) {
            this.$router.push({ name: 'editStory', params: { id: storyId } });
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return this.$moment(dateString).fromNow();
        },
        getPageBackgroundStyle(story, pageIndex) {
            if (story.pages && story.pages[pageIndex] && story.pages[pageIndex].muxAsset && story.pages[pageIndex].muxAsset.playbackId) {
                const playbackId = story.pages[pageIndex].muxAsset.playbackId;
                return `background-image: url(https://image.mux.com/${playbackId}/thumbnail.jpg?time=0); background-size: cover; background-position: center;`;
            }
            
            if (story.muxAsset && story.muxAsset.playbackId) {
                const playbackId = story.muxAsset.playbackId;
                return `background-image: url(https://image.mux.com/${playbackId}/thumbnail.jpg?time=0); background-size: cover; background-position: center;`;
            }
            
            return 'background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);';
        }
    }
};
</script>

<style scoped>
.spinner {
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
}

.spinner-small {
    border: 1px solid #f3f3f3;
    border-top: 1px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.story-grid-container {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
}

.story-grid-container::-webkit-scrollbar {
    height: 8px;
}

.story-grid-container::-webkit-scrollbar-track {
    background: #f7fafc;
    border-radius: 4px;
}

.story-grid-container::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
}

.story-grid-container::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

.story-card {
    overflow: visible !important;
    width: 200px;
}

.story-container {
    position: relative;
    perspective: 1000px;
}

.fan-out-pages {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.fan-page {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
    background-size: cover;
    background-position: center;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease-out;
    z-index: 0;
}

.fan-page-left {
    left: -4px;
    transform: translateZ(-20px) rotate(-2deg);
}

.fan-page-right {
    left: 4px;
    transform: translateZ(-10px) rotate(2deg);
}

.main-video-card {
    position: relative;
    z-index: 10;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease-out;
}

.story-card:hover .fan-page-left {
    left: -8px;
    transform: translateZ(-20px) rotate(-4deg);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.story-card:hover .fan-page-right {
    left: 8px;
    transform: translateZ(-10px) rotate(4deg);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.story-card:hover .main-video-card {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}
</style>
